import { useEnvStore } from '@remote/store/env'
import { defineStore } from 'pinia'
import { computed, Ref, ref } from 'vue'
import { API } from '@remote/api/nfo'
// import { message } from '@remote/utils/util'
// import { store } from '@remote/store'
// import { openDeleteNFOConfirmDialog } from '@remote/utils/dialog'
import { stat } from '@remote/utils/stat'
import { useCinemaStoreCommon } from '@shared/composable/cinema'
import { SECOND } from '@shared/constants/time'

export * from './helper'
export type CinemaTab = 'INDEX' | 'MOVIE' | 'TV_SERIES' | 'OTHER'

const CINEMA_TABS: readonly { id: CinemaTab; title: string }[] = Object.freeze([
  {
    id: 'INDEX',
    title: '首页',
  },
  {
    id: 'MOVIE',
    title: '电影',
  },
  {
    id: 'TV_SERIES',
    title: '电视剧',
  },
  {
    id: 'OTHER',
    title: '其他',
  },
])

export const useCinemaStore = defineStore('cinema', () => {
  /** 个人影院Tab里的二级Tab，分类 */
  const currentTab = ref<CinemaTab>('INDEX')
  const cinemaTabs = ref(CINEMA_TABS)

  /** 是否正在播放视频 */
  const isPlaying = ref(false)
  function setPlaying (playing: boolean) {
    isPlaying.value = playing
  }

  const envStore = useEnvStore()
  const targetRef = computed(() => envStore.space)

  const hookAfterScrape = async (from: string) => {
    const res = await API.getNFOList({
      class: 'movie',
      last_page_token: '',
      page_size: 0,
    })
    stat({
      action: 'cinema_scraper_finish',
      data: {
        from,
        duration: Math.floor((new Date(res.runtime?.end_time ?? 0).valueOf() - new Date(res.runtime?.start_time ?? 0).valueOf()) / SECOND),
        checked_path_num: res.runtime?.dir_total_count,
        video_num: res.runtime?.video_total_count,
      },
    })
  }

  const scrapeVersion = { initialized: false, prev: 0, current: 0, hasUpdate: false}

  const getScrapeVersion = async (caller = ''):Promise<{initialized:boolean; hasUpdate:boolean}> => {
    const { initialized } = scrapeVersion
    const [error, result] = await API.getScrapeVersion()
    if (!error && result?.ret === 0 && result?.data !== void 0) {
      if (initialized) {
        // 计算是否刮削版本变更要执行刷新
        scrapeVersion.hasUpdate = scrapeVersion.current !== result.data
      } else {
        scrapeVersion.initialized = true
      }
      scrapeVersion.prev = scrapeVersion.current
      scrapeVersion.current = result.data
      console.log('getScrapeVersion', caller, result, scrapeVersion)
    }
    return {hasUpdate: scrapeVersion.hasUpdate, initialized}
  }

  const {
    isScraping,
    lastScrapeTimestamp,
    createScrapeTask,
    isRecentListEnd,
    isMovieListEnd,
    isTvListEnd,
    isOtherListEnd,
    loadRecentNFOList,
    loadMovieNFOList,
    loadTvNFOList,
    loadOtherNFOList,
    recentList,
    movieList,
    tvList,
    otherList,
    refreshRecentNFOList,
    refreshMovieNFOList,
    refreshOtherNFOList,
    refreshTvNFOList,
    refreshScrapeTaskStatus,
    submitFeedback,
    refreshAllList,
  } = useCinemaStoreCommon({ API, targetRef, hookAfterScrape })

  /**
   * 调用删除刮削项
   * 接口返回成功后，删除对应列表中的项。
   */
  const deleteNFO = async (item: API_NFO.NFO, fileIds: string[]) => {
    const itemClass = item.scrape_result?.class ?? 'movie'
    const classListMap: { [key in API_NFO.NFOClass]: Ref<API_NFO.NFO[]> } = {
      movie: movieList,
      other: otherList,
      tv: tvList,
    }
    const currentOperatingList = classListMap[itemClass]
    if (item?.scrape_result?.id !== null && item?.scrape_result?.id !== undefined) {
      const [error, result] = await API.deleteNFO({ file_id: fileIds})
      if (!error && result?.ret === 0) {
        // 把store中的这一条数据清除掉
        const index = currentOperatingList.value.findIndex(i => i.scrape_result?.id === item.scrape_result?.id)
        if (index > -1) {
          currentOperatingList.value.splice(index, 1)
        }

        // 把最近观看中的这一条数据清除掉
        const recentListIndex = recentList.value.findIndex(i => i.scrape_result?.id === item.scrape_result?.id)
        if (recentListIndex > -1) {
          recentList.value.splice(recentListIndex, 1)
        }
      } else {
        return error?.error_description || error?.msg || result.message
      }
    }
    return
  }

  /**
   * 选择个人影院中展示的Tab
   */
  const selectTab = (tab: CinemaTab) => {
    currentTab.value = tab
  }

  return {
    currentTab,
    cinemaTabs,
    isScraping,
    lastScrapeTimestamp,
    selectTab,
    recentList,
    isRecentListEnd,
    loadRecentNFOList,
    refreshRecentNFOList,
    movieList,
    isMovieListEnd,
    loadMovieNFOList,
    refreshMovieNFOList,
    tvList,
    isTvListEnd,
    loadTvNFOList,
    refreshTvNFOList,
    otherList,
    isOtherListEnd,
    loadOtherNFOList,
    refreshOtherNFOList,
    createScrapeTask,
    refreshScrapeTaskStatus,
    deleteNFO,
    submitFeedback,
    refreshAllList,
    isPlaying,
    setPlaying,
    getScrapeVersion,
  }
})